<template>
  <WorkBaseRestrictedTextField
    ref="restrictedTextField"
    class="w-decimal-field"
    :value="value"
    v-bind="$props"
    :char-whitelist="internalCharWhiteList"
    :always-allowed-keys="internalAlwaysAllowedKeys"
    :placeholder="internalPlaceholder"
    :array-of-is-valid-functions="validations"
    :input-get-formatter="invokeGetFormatter"
    :input-set-formatter="invokeSetFormatter"
    :format-value-on-focus-out="invokeFormatOnFocusOut"
    :format-value-on-focus-in="invokeFormatOnFocusIn"
    :read-only-formatter="invokeReadonlyFormat"
    :space-between="spaceBetween"
    @input="$emit('input', $event)"
    @change="$emit('change', $event)"
    @blur="$emit('blur', $event)"
    @click="$emit('click', $event)"
  />
</template>

<script>
// components
import WorkBaseRestrictedTextField from '@/components/global_components/forms/text_fields/WorkBaseRestrictedTextField';

// helpers
import { DECIMAL_CONFIG } from '@/services/formatter_services/enums/DECIMAL_CONFIG';
import {
  formatDecimalStringToNumber,
  formatNumberToDecimalString
} from '@/services/formatter_services/decimal-formatter';

// mixins
import numberFieldMixin from '@/components/global_components/forms/text_fields/mixins/number-field-mixin';
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';

/**
 * @component WorkDecimalTextField
 * @description Decimal input field with localized formatting.
 * Automatically converts between decimal string and numeric value.
 *
 * @prop {Number|String} [value] - Current numeric value (v-model).
 * @prop {String} [locale='de'] - Locale for decimal separator formatting.
 * @prop {Boolean} [spaceBetween=false] - Space between field and buttons.
 *
 * @emits {Number|null} input - Emitted when the value changes.
 */
export default {
  name: 'WorkDecimalTextField',
  components: { WorkBaseRestrictedTextField },
  props: {
    value: { type: [Number, String] },
    locale: { type: String, default: 'de' },
    spaceBetween: { type: Boolean, default: false }
  },
  mixins: [
    isRequired,
    pendingAble,
    numberFieldMixin(
      {
        getFormatter: (v, self) => formatNumberToDecimalString(v, self.locale),
        setFormatter: (v, self) => formatDecimalStringToNumber(v, self.locale)
      },
      true
    )
  ],
  computed: {
    validations() {
      return DECIMAL_CONFIG?.[this.locale]?.validations ?? [];
    },
    internalPlaceholder() {
      if (this.placeholder != undefined) {
        return this.placeholder;
      }
      return formatNumberToDecimalString(0, this.locale);
    }
  },
  methods: {
    focus() {
      this.$refs.restrictedTextField.focus();
    }
  }
};
</script>

<style lang="scss" scoped>
.w-decimal-field ::v-deep {
  .v-text-field__slot input {
    text-align: end !important;
  }
}
</style>
